import { FC, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, TextField, InputAdornment, Typography, Pagination, ToggleButton, ToggleButtonGroup, Autocomplete, Paper } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useQuery } from 'react-query';
import clsx from 'clsx';

import useLocalStorage from 'hooks/useLocalStorage';
import { getAuthors, getVideosNew, getTags, getLevels } from 'api/videos';

import Select from 'components/Select/Select';
import VideoCard from './components/VideoCard/VideoCard';
import Playback from './Playback/Playback';

const watchedFilter = [
    { title: 'Все', tag: 'all' },
    { title: 'Просмотренные', tag: 'watched' },
    { title: 'Не просмотренные', tag: 'new' },
    { title: 'Избранные', tag: 'favorite' },
];

const Video: FC = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const [pageSize, setPageSize] = useLocalStorage('video_page_size', '10');
    const [search, setSearch] = useState('');
    const [sort, setSort] = useState({ value: 'desc', label: 'Сначала новые' });
    const [authors, setAuthors] = useState([]);
    const [tags, setTags] = useState([]);
    const [accessLevels, setAccessLevels] = useState([]);
    const [page, setPage] = useState(1);
    const [watched, setWatched] = useState('all');
    const [selectVideo, setSelectVideo] = useState<string | undefined>(undefined);

    const { data, isLoading } = useQuery(['videos', authors, search, tags, accessLevels, pageSize, page, sort, watched], getVideosNew);
    const { data: authorsList, isLoading: authorsLoading } = useQuery('videos-authors', getAuthors);
    const { data: tagsList, isLoading: tagsLoading } = useQuery(['videos-tags'], getTags);
    const { data: levelList, isLoading: levelLoading } = useQuery(['videos-levels'], getLevels);

    useEffect(() => {
        const paramsId = new URLSearchParams(location.search).get('id');
        if (paramsId) setSelectVideo(paramsId);
    }, [data, location]);

    useEffect(() => {
        setPage(1);
    }, [pageSize, watched, accessLevels, tags, authors, sort, search]);

    const goToVideo = video => {
        navigate({ pathname: '/video', search: `?id=${video.id}` });
    };

    const backToList = () => {
        navigate({ pathname: '/video', search: `` });
        setSelectVideo(undefined);
    };

    if (selectVideo !== undefined) {
        return <Playback id={selectVideo} resetVideo={backToList} />;
    }

    return (
        <div className="w-full">
            <Paper className="p-4 space-y-4">
                <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2}>
                    <TextField
                        label="Поиск"
                        value={search}
                        onChange={({ target }) => setSearch(target.value)}
                        fullWidth
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Autocomplete
                        multiple
                        limitTags={1}
                        options={authorsList ?? []}
                        getOptionLabel={(option: { name: string; id: string }) => option?.name}
                        renderInput={params => <TextField {...params} label="Автор" />}
                        onChange={(_, value) => setAuthors(value)}
                        value={authors}
                        disabled={authorsLoading}
                        fullWidth
                    />
                    <Autocomplete
                        multiple
                        limitTags={1}
                        options={tagsList ?? []}
                        getOptionLabel={(option: { name: string; id: string }) => option?.name}
                        renderInput={params => <TextField {...params} label="Теги" />}
                        onChange={(_, value) => setTags(value)}
                        disabled={tagsLoading}
                        value={tags}
                        fullWidth
                    />
                    <Autocomplete
                        multiple
                        limitTags={1}
                        options={levelList ?? []}
                        getOptionLabel={(option: { name: string; id: string }) => option?.name}
                        renderInput={params => <TextField {...params} label="Уровень" />}
                        onChange={(_, value) => setAccessLevels(value)}
                        disabled={levelLoading}
                        value={accessLevels}
                        fullWidth
                    />
                    <Select
                        label="Сортировка"
                        options={[
                            { value: 'desc', label: 'Сначала новые' },
                            { value: 'asc', label: 'Сначала старые' },
                        ]}
                        value={sort}
                        onChange={setSort}
                        multiple={false}
                    />
                    <Select
                        label="Размер страницы"
                        options={['10', '25', '50', '100', 'Все']}
                        value={pageSize}
                        onChange={val => setPageSize(val)}
                        multiple={false}
                    />
                </Box>
            </Paper>

            <ToggleButtonGroup
                value={watched}
                exclusive
                onChange={(_, newValue) => {
                    if (newValue !== null) {
                        setWatched(newValue);
                        setPage(1);
                    }
                }}
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: { xs: 'center', sm: 'flex-start' },
                    my: 3,
                }}
            >
                {watchedFilter.map(val => (
                    <ToggleButton
                        key={val.tag}
                        value={val.tag}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 500,
                            fontSize: '0.8rem',
                            minHeight: '28px',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: '6px',
                            color: 'text.primary',
                            borderColor: '#ccc',
                            '&.Mui-selected': {
                                bgcolor: 'primary.main',
                                color: '#fff',
                                borderColor: 'primary.main',
                                '&:hover': {
                                    bgcolor: 'primary.dark',
                                },
                            },
                        }}
                    >
                        {val.title}
                    </ToggleButton>
                ))}
            </ToggleButtonGroup>

            {isLoading && (
                <div className="flex flex-1 items-center justify-center">
                    <CircularProgress />
                </div>
            )}

            {!data?.videos && !isLoading && (
                <div className="flex flex-1 items-center justify-center">
                    <Typography variant="h4">Список видеозаписей пуст</Typography>
                </div>
            )}

            <div className="grid lg:grid-cols-4 md:grid-cols-2 grid-cols-1 gap-5">
                {!isLoading && data?.videos?.map(video => <VideoCard key={video.id} video={video} selectVideo={goToVideo} />)}
            </div>

            {data?.count && (
                <div className="w-full mt-10 flex justify-center">
                    <Pagination count={Math.ceil(data.count / +pageSize)} size="large" page={page} onChange={(_, newPage) => setPage(newPage)} />
                </div>
            )}
        </div>
    );
};

export default Video;
